import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { OrderStore } from './order-store';

/** The four booking-flow steps. Everywhere else keeps the default palette. */
const FLOW_ROUTES = ['/delivery-request', '/add-to-cart', '/address-details', '/checkout'];

/**
 * Recycle runs on green instead of teal. Marking <html> lets the token override
 * in _brand.scss repaint the whole flow at once, Material overlays included,
 * rather than every component having to know which service it belongs to.
 */
@Injectable({ providedIn: 'root' })
export class FlowTheme {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly orderStore = inject(OrderStore);
  private readonly router = inject(Router);

  private readonly url = signal(this.router.url);

  // 2 = Recycle. Scoped to the flow so picking Recycle does not turn the
  // marketing pages green on the way back out.
  private readonly isRecycleFlow = computed(
    () => this.orderStore.orderData().serviceType === 2 && this.isFlowRoute(this.url()),
  );

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => this.url.set(event.urlAfterRedirects));

    effect(() => {
      const root = this.document.documentElement;

      if (this.isRecycleFlow()) {
        root.setAttribute('data-service', 'recycle');
      } else {
        root.removeAttribute('data-service');
      }
    });
  }

  private isFlowRoute(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return FLOW_ROUTES.some((route) => path === route || path.startsWith(`${route}/`));
  }
}
